import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';

// Move the real content between its measured positions. No screenshot layer,
// duplicated author or remounted player is needed for the transition.
const parts = ['.storyTitle', '.post-user', '.reaction_content_text', '.reaction-chain', '.reaction-actions__read'];

export default function useReaderMotion() {
  const ref = useRef(null);
  const animations = useRef([]);
  const cancel = () => {
    animations.current.forEach(animation => animation.cancel());
    animations.current = [];
  };
  useEffect(() => () => cancel(), []);

  const transition = update => {
    const card = ref.current;
    if (!card?.animate || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      cancel();
      update();
      return;
    }
    // Capture before cancelling: a second tap reverses from the visible frame.
    const before = parts.map(selector => card.querySelector(selector)?.getBoundingClientRect().top);
    cancel();
    flushSync(update);
    const style = getComputedStyle(card);
    const duration = parseFloat(style.getPropertyValue('--reader-motion-duration')) || 220;
    const easing = style.getPropertyValue('--reader-motion-easing').trim() || 'cubic-bezier(.2, 0, 0, 1)';
    // Read every destination before writing animation styles, avoiding a
    // separate style/layout flush for each author/text/quote block.
    const moves = parts.map((selector, index) => {
      const element = card.querySelector(selector);
      return element && before[index] !== undefined
        ? { element, distance: before[index] - element.getBoundingClientRect().top } : null;
    }).filter(Boolean);
    moves.forEach(({ element, distance }) => {
      // Closing after a long scroll returns to the start without sweeping
      // several screens of text past the user.
      const offset = Math.abs(distance) > 160 ? Math.sign(distance) * 24 : distance;
      if (Math.abs(offset) < .5) return;
      const animation = element.animate([
        { transform: `translateY(${offset}px)`, opacity: Math.abs(distance) > 160 ? .85 : 1 },
        { transform: 'translateY(0)', opacity: 1 },
      ], { duration, easing });
      animations.current.push(animation);
    });
  };
  return { ref, transition };
}
